import React, {
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
} from "react";
import { useHistory, useParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";

import {
  getDictionaryUuidFromCurrentUrl,
  isStringUuid,
  customEqual,
} from "src/utils/methods";
import WebsocketListener from "src/WebsocketListener";
import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import NoResultsScreen from "src/app/components/NoResultsScreen/NoResultsScreen";
import noResultsImg from "src/images/create-product.svg";
import noFilteredResultsImg from "src/images/empty-folder-group.svg";
import { ReactComponent as PlusSignIcon } from "src/images/plus-white.svg";
import { SidebarItemType } from "src/types";
import { getDictionaryList, clearDictionaryList } from "src/redux";
import { ROUTES, getRoute } from "src/utils/ROUTES";
import { uuidv7 } from "uuidv7";
import { openDictionaryDetails } from "../components/DictionaryDetails/methods";
import { useQuery } from "../methods/useQuery";
import DictionaryDetails from "../components/DictionaryDetails/DictionaryDetails";
import { Button } from "../components/Button/Button";
import IDHFormattedMessage from "../components/IDHFormattedMessage/IDHFormattedMessage";
import DictionarySortDropdown from "../components/DictionarySortDropdown/DictionarySortDropdown";
import { ReactComponent as FilterIcon } from "../../images/filter.svg";
import dictionaryFiltersReducer, {
  dictionaryFiltersReducerInitialState,
} from "./redux/dictionaryFiltersReducer";
import {
  setDictionaryFilters,
  resetDictionaryFiltersReducer,
  setDictionaryInitialColumnRanks,
  resetHiddenColumns,
  setSelectedTableView,
  setDictionarySort,
  setDictionarySortBy,
  setHiddenDictionaryColumnIds,
} from "../components/DictionaryFilters/redux/dictionaryFiltersActions";
import { DictionaryFilters } from "../components/DictionaryFilters/DictionaryFilters";
import GlideDataGridWrapper from "../components/GlideDataGrid/GlideDataGridWrapper";
import MetaDataOptionsDropdown, {
  MetaDataOptionsContext,
} from "../components/TableOptionsDropdown/MetaDataOptionsDropdown";
import {
  generateLackingRanks,
  generateMetadataRanks,
} from "../CreatorDatabase/redux/creatorDatabaseFiltersReducer";
import { tableDataType } from "../components/Table/Table";
import { FiltersContext } from "../components/DictionaryFilters/dictionaryFiltersData";
import TableViewSelector from "../TableView/TableViewSelector";
import { createDictionaryTableView } from "../TableView/tableViewFunctions";
import DictionaryTableAddNewDictionaryElementModal from "./components/AddNewDictionaryElementModalDictionaryTable";
import { usePageTitle } from "../title-management/usePageTitle";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "src/utils/localStorage";

export default function Dictionary() {
  const activeWorkspaceUuid = useSelector(
    (state) => state.mainReducer.activeWorkspaceUuid,
  );
  const identity = useSelector(
    (state) => state.mainReducer.identity,
    customEqual,
  );
  const dictionaryList = useSelector(
    (state) => state.dictionaryReducer.dictionaryList,
    customEqual,
  );
  const isDictionaryListLoading = useSelector(
    (state) => state.dictionaryReducer.isDictionaryListLoading,
  );
  const { wsTableViews } = dictionaryList;

  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();
  const showDiscoveryDetails = isStringUuid(
    query.get("displayDictionaryPreview") || "",
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [dictionaryFilters, dispatchDF] = useReducer(
    dictionaryFiltersReducer,
    dictionaryFiltersReducerInitialState(dictionaryList.columns),
  );
  const [elementToScrollUuid, setElementToScrollUuid] = useState(null);
  const [initialRankGenerationDone, setInitialRankGenerationDone] =
    useState(false);

  const {
    filters,
    hiddenColumns,
    dictionaryColumnRanks,
    selectedTableView = {
      label: "Custom",
      value: null,
    },
  } = dictionaryFilters;

  const params = useParams();

  const foundDictionarySidebarItem = identity?.sidebarItems?.find(
    (item) =>
      item.type === SidebarItemType.Dictionary &&
      item.wsDictionaryUuid === params.dictionaryUuid,
  );

  const header = foundDictionarySidebarItem?.wsDictionaryName;

  const description = `Here you can manage a database of ${header.toLowerCase()}.`;

  usePageTitle(header);

  const filtersLength =
    Object.keys(filters || {}).length > 0 &&
    `: ${Object.keys(filters || {}).length}`;

  const handleGetDictionaryList = useCallback(
    (dictionaryUuid, filtersObj) => {
      dispatch(getDictionaryList(dictionaryUuid, filtersObj));
    },
    [dispatch],
  );

  const handleRowNameClick = (dictionaryElementUuid) => {
    openDictionaryDetails(history, dictionaryElementUuid);
  };

  const handleRestoreDefault = () => {
    const settingsData = getFromLocalStorage("ws-dictionary-settings");

    if (!settingsData) {
      return;
    }

    delete settingsData[activeWorkspaceUuid];

    saveToLocalStorage("ws-dictionary-settings", settingsData);

    dispatchDF(
      setDictionaryInitialColumnRanks(
        generateMetadataRanks(dictionaryList.columns),
      ),
    );
    dispatchDF(resetHiddenColumns());
    setInitialRankGenerationDone(true);
  };

  const saveTableView = async (name) => {
    const visibleColumns = dictionaryList.columns
      .filter((field) => !hiddenColumns?.includes(field.uuid))
      .map((field) => ({ uuid: field.uuid, exampleKey: "aaaa" }));

    const uuid = uuidv7();

    await createDictionaryTableView({
      filters: dictionaryFilters.filters,
      sortBy: dictionaryFilters.sortBy,
      sort: dictionaryFilters.sort,
      visibleColumns,
      wsWorkspaceUuid: activeWorkspaceUuid,
      wsDictionaryUuid: params.dictionaryUuid,
      name,
      uuid,
    });
    handleGetDictionaryList(params.dictionaryUuid, dictionaryFilters);
    dispatchDF(
      setSelectedTableView({
        label: name,
        value: uuid,
      }),
    );
  };

  const renderHeaderToolbar = () => (
    <>
      {dictionaryList.list.length > 0 && (
        <Button
          size="small"
          textWithIcon
          variant="blue"
          onClick={() => setIsModalOpen(true)}
        >
          <PlusIcon />
          <span>
            <IDHFormattedMessage id="ws_add" defaultMessage="Add" />
            &nbsp;
            {foundDictionarySidebarItem?.wsDictionaryElementName &&
              foundDictionarySidebarItem?.wsDictionaryElementName}
          </span>
        </Button>
      )}
      <Button
        variant="white"
        size="small"
        textWithIcon
        onClick={() => setShowFilters((prev) => !prev)}
        active={Object.keys(filters || {}).length > 0}
        data-qa="filter-button"
      >
        <FilterIcon />
        <span>
          <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
          {filtersLength}
        </span>
      </Button>
      {dictionaryList.list.length > 0 && (
        <DictionarySortDropdown
          fields={dictionaryList.columns}
          dictionaryFilters={dictionaryFilters}
          dispatchDictionaryFilters={dispatchDF}
        />
      )}

      <MetaDataOptionsDropdown
        context={MetaDataOptionsContext.Dictionary}
        fields={dictionaryList.columns}
        fieldRanks={dictionaryColumnRanks}
        hiddenColumns={hiddenColumns}
        dispatchFunction={dispatchDF}
        resetToDefaultSettings={handleRestoreDefault}
      />

      <TableViewSelector
        wsTableViews={wsTableViews}
        selectedWsTableView={selectedTableView}
        setSelectedWsTableView={(newValue) =>
          dispatchDF(setSelectedTableView(newValue))
        }
        setFilters={(newFilters, skipLS) =>
          dispatchDF(setDictionaryFilters(newFilters, skipLS))
        }
        setHiddenOrVisibleColumnIds={(columnIds, skipLS) =>
          dispatchDF(setHiddenDictionaryColumnIds(columnIds, skipLS))
        }
        setSortBy={(newValue, skipLS) =>
          dispatchDF(setDictionarySortBy(newValue, skipLS))
        }
        setSort={(newValue, skipLS) =>
          dispatchDF(setDictionarySort(newValue, skipLS))
        }
        columnIds={dictionaryList?.columns?.map((field) => field.uuid)}
        localStorageKey="ws-dictionary-settings"
        saveTableViewFunction={saveTableView}
        reloadDataFunction={() =>
          handleGetDictionaryList(params.dictionaryUuid, dictionaryFilters)
        }
      />
    </>
  );

  useEffect(() => {
    if (!params.dictionaryUuid || !dictionaryFilters) return;
    handleGetDictionaryList(params.dictionaryUuid, dictionaryFilters);
  }, [params.dictionaryUuid, dictionaryFilters, handleGetDictionaryList]);

  useEffect(() => {
    if (initialRankGenerationDone) return;
    const settingsData = JSON.parse(
      localStorage.getItem("ws-dictionary-settings"),
    );

    let settingsForWorkspace;

    if (settingsData) {
      settingsForWorkspace = settingsData[activeWorkspaceUuid];
    }

    const settingsForDictionary = settingsForWorkspace
      ? settingsForWorkspace[getDictionaryUuidFromCurrentUrl()]
      : { dictionaryColumnRanks: [] };

    if (
      dictionaryList?.columns?.length &&
      !settingsForDictionary?.dictionaryColumnRanks?.length
    ) {
      dispatchDF(
        setDictionaryInitialColumnRanks(
          generateMetadataRanks(dictionaryList.columns),
        ),
      );
      setInitialRankGenerationDone(true);
    } else if (
      dictionaryList?.columns.length &&
      dictionaryList?.columns.length !== dictionaryColumnRanks.length
    ) {
      dispatchDF(
        setDictionaryInitialColumnRanks(
          generateLackingRanks(
            dictionaryList?.columns,
            settingsForDictionary?.dictionaryColumnRanks,
          ),
        ),
      );
      setInitialRankGenerationDone(true);
    }
  }, [
    activeWorkspaceUuid,
    initialRankGenerationDone,
    dictionaryList.columns,
    dictionaryColumnRanks,
  ]);

  useEffect(() => {
    dispatchDF(resetDictionaryFiltersReducer());
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearDictionaryList());
    dispatchDF(resetDictionaryFiltersReducer());
  }, [dispatch]);

  const filteredColumns = useMemo(
    () =>
      dictionaryList.columns
        .filter((col) => !hiddenColumns?.includes(col.uuid))
        .sort((a, b) => {
          const rankA = dictionaryColumnRanks?.find(
            (col) => col.uuid === a.uuid,
          )?.rank;
          const rankB = dictionaryColumnRanks?.find(
            (col) => col.uuid === b.uuid,
          )?.rank;

          if (rankA && rankB) return rankA.localeCompare(rankB);
          return 0;
        }),
    [dictionaryList.columns, hiddenColumns, dictionaryColumnRanks],
  );

  return (
    <>
      <GlideDataGridWrapper
        header={header}
        description={description}
        columnsData={filteredColumns}
        rowsData={dictionaryList.list}
        isLoading={isDictionaryListLoading}
        titleColumnLabel={foundDictionarySidebarItem?.wsDictionaryElementName}
        context={tableDataType.Dictionary}
        renderHeaderToolbar={renderHeaderToolbar}
        handleRowNameClick={handleRowNameClick}
        filtersAndSortingOptions={dictionaryFilters}
        filtersAndSortingOptionsDispatch={dispatchDF}
        elementToScrollUuid={elementToScrollUuid}
        setElementToScrollUuid={setElementToScrollUuid}
        totalRowsCount={dictionaryList.totalCount}
        noResultsScreen={
          <NoResultsScreen
            title={
              <span>
                <IDHFormattedMessage
                  id="ws_there_are_no"
                  defaultMessage="There are no"
                />{" "}
                {foundDictionarySidebarItem?.wsDictionaryName}
              </span>
            }
            subtitle={
              <span>
                <IDHFormattedMessage
                  id="ws_add_your_first"
                  defaultMessage="Add your first"
                />{" "}
                {foundDictionarySidebarItem?.wsDictionaryElementName}
              </span>
            }
            imgUrl={noResultsImg}
            bottomContent={
              <Button
                size="large"
                variant="blue"
                onClick={() => setIsModalOpen(true)}
              >
                <PlusSignIcon />
                <span>
                  <IDHFormattedMessage id="ws_add" defaultMessage="Add" />{" "}
                  {foundDictionarySidebarItem?.wsDictionaryElementName}
                </span>
              </Button>
            }
          />
        }
        noFilteredResultsScreen={
          <NoResultsScreen
            title={
              <IDHFormattedMessage
                id="ws_no_results_found"
                defaultMessage="No results found"
              />
            }
            subtitle={
              <IDHFormattedMessage
                id="ws_remove_filters"
                defaultMessage="No results match the filter criteria. Remove filter or clear all filters to show results."
              />
            }
            imgUrl={noFilteredResultsImg}
            bottomContent={
              <Button
                size="large"
                variant="blue"
                onClick={() => dispatchDF(setDictionaryFilters([]))}
              >
                <IDHFormattedMessage
                  id="ws_clear_filters"
                  defaultMessage="Clear filters"
                />
              </Button>
            }
          />
        }
        openGlideElement={(elementUuid) =>
          getRoute(
            ROUTES.DICTIONARY_ELEMENT_DETAILS,
            {
              wsWorkspaceUuid: activeWorkspaceUuid,
              wsDictionaryUrl: foundDictionarySidebarItem?.wsDictionaryName,
              wsDictionaryUuid: params.dictionaryUuid,
            },
            { displayDictionaryPreview: elementUuid },
          )
        }
      />

      <DictionaryFilters
        context={FiltersContext.Dictionary}
        dictionaryFilters={dictionaryFilters}
        dispatchDictionaryFilters={dispatchDF}
        setFiltersVisible={setShowFilters}
        filtersVisible={showFilters}
        columns={dictionaryList.columns}
      />

      {showDiscoveryDetails && (
        <DictionaryDetails
          tableViewsFunctions={{
            hiddenColumns,
            dictionaryColumnRanks,
            selectedWsTableView: selectedTableView,
            setSelectedWsTableView: (newValue) =>
              dispatchDF(setSelectedTableView(newValue)),
            setFilters: (newFilters) =>
              dispatchDF(setDictionaryFilters(newFilters)),
            setHiddenOrVisibleColumnIds: (columnIds) =>
              dispatchDF(setHiddenDictionaryColumnIds(columnIds)),
            setSortBy: (newValue) => dispatchDF(setDictionarySortBy(newValue)),
            setSort: (newValue) => dispatchDF(setDictionarySort(newValue)),
          }}
        />
      )}
      {isModalOpen && (
        <DictionaryTableAddNewDictionaryElementModal
          onClose={() => setIsModalOpen(false)}
          dictionaryUuid={params.dictionaryUuid}
          setElementToScrollUuid={setElementToScrollUuid}
        />
      )}

      <WebsocketListener />
    </>
  );
}
